import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '../components/ui'
import { useAuth } from '../components/AuthProvider'
import { getActiveOrgId } from '../utils/orgUtils'
import { useI18n } from '../i18n'
import { API_ENDPOINTS } from '../config/api'
import scanService, { type ScanStatus } from '../services/scanService'
import { safeJson } from '../lib/utils'
import { Server, Shield, AlertTriangle, CheckCircle, Clock, Globe, Database, Monitor, RefreshCw, ChevronDown, ChevronUp, Scan, Play } from 'lucide-react'
import AssetsOverview from '../components/assets/AssetsOverview'
import AssetsInventoryTable from '../components/assets/AssetsInventoryTable'
import SecurityControlsCoverageCard from '../components/assets/SecurityControlsCoverageCard'
import type { SecurityProtectionCoverage } from '../services/securityControlCoverageApi'
import {
  parseSecurityControlInventoryFilters,
  assetMatchesSecurityControlFilters,
  hasActiveSecurityControlFilters,
  clearSecurityControlParams,
  removeSecurityControlParam,
  buildSecurityControlSearchParams,
  type SecurityControlInventoryFilters,
  type BuildInventoryUrlOpts,
} from '../services/securityControlInventoryFilters'
import VulnerabilityExposureCard from '../components/assets/VulnerabilityExposureCard'
import AssetSystemAccountabilityCard from '../components/assets/AssetSystemAccountabilityCard'
import type { AssetSystemAccountability } from '../components/assets/AssetSystemAccountabilityCard'
import {
  parseAssetSystemAccountabilityFilters,
  assetMatchesAccountabilityFilters,
  hasActiveAccountabilityFilters,
  clearAccountabilityParams,
  removeAccountabilityParam,
  buildAccountabilitySearchParams,
  type AssetSystemAccountabilityFilters,
  type AccountabilityHrefOpts,
} from '../services/assetSystemAccountabilityFilters'
import ActiveScansCard from '../components/assets/ActiveScansCard'
import OpenVASScanCard from '../components/assets/OpenVASScanCard'
import AddAssetCard from '../components/assets/AddAssetCard'
import EditAssetCard from '../components/assets/EditAssetCard'
import AssetsInventoryCard from '../components/assets/AssetsInventoryCard'
import { Button } from '../components/ui/Button'
import entStyles from '../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { AssetsWorkspace, type AssetsWorkspaceTab } from '../components/assets/AssetsWorkspace'
import AssetInventoryTab from '../components/assets/AssetInventoryTab'
import AssetInventoryIntelligenceWorkspace from '../components/assets/AssetInventoryIntelligenceWorkspace'
import {
  AssetInventoryExecutiveProvider,
  AssetInventoryPostureSection,
  AssetInventoryGraphSignalsSection,
  AssetInventoryListSection,
} from '../components/enterprise-operational/AssetInventoryExecutiveWorkspace'
import { assetMatchesCategoryView } from '../constants/assetTaxonomy'
import EnterpriseOperationalPage from '../components/enterprise-operational/EnterpriseOperationalPage'
import { featureFlags } from '../config/featureFlags'
import { useCurrentVulnerabilityPosture } from '../hooks/useCurrentVulnerabilityPosture'
import type { AssetOverviewStats } from '../components/enterprise-operational/assetInventoryExecutiveContext'
import { resolveAssetScanHost, formatAssetTargetLabel } from '../utils/gvmHosts'
import type { AssetCategoryView } from '../constants/assetTaxonomy'
import InformationSystemsTab from '../components/assets/InformationSystemsTab'
import type { PassportSection } from '../components/assets/informationSystemPassportTypes'
import DataMapTab from '../components/assets/DataMapTab'
import RiskComplianceTab from '../components/assets/RiskComplianceTab'
import InformationSystemInventoryFilter from '../components/assets/InformationSystemInventoryFilter'
import { fetchInformationSystem } from '../services/informationSystemsApi'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts'
import { PageLayout } from '../layouts/PageLayout'
import ScanJobsResultsSection from '../components/assets/ScanJobsResultsSection'
import assetsPageStyles from '../styles/pages/Assets.module.css'
import assetsHeaderStyles from '../styles/pages/AssetsPageHeader.module.css'
import CloudAssetProposalsCard from '../components/assets/CloudAssetProposalsCard'

interface Asset {
  id: string
  assetId: string
  name: string
  type: string
  environment: string
  ipAddress: string
  standards: string[]
  complianceScore: number
  riskLevel: string
  lastAssessment: string
  vulnerabilities: {
    critical: number
    high: number
    medium: number
    low: number
    total: number
  }
  // New fields
  criticality?: string
  riskScore?: number
  createdAt?: string
  owner?: {
    id: string
    name: string
    department?: string
    email?: string
  }
  lastScan?: string
  posture?: {
    os?: string
    patchLevel?: number
    edrInstalled: boolean
    firewallEnabled: boolean
    encryptionEnabled: boolean
  }
  securityControlCoverage?: {
    controls?: Array<{
      controlType: string
      status: string
      verificationState?: string
      sourceType?: string
      sourceProvider?: string | null
      lastCheckedAt?: string | null
    }>
  }
  unknownControlsCount?: number
  disabledControlsCount?: number
  verifiedControlsCount?: number
  knownControlsCount?: number
  lastSecurityControlObservationAt?: string | null
  securityCoverageStatus?: string
  hasOwner?: boolean
  linkedToInformationSystem?: boolean
  ownerId?: string | null
}

const Assets: React.FC = () => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { token, user, refreshUser } = useAuth()
  const [assets, setAssets] = useState<Asset[]>([])
  const [securityProtectionCoverage, setSecurityProtectionCoverage] =
    useState<SecurityProtectionCoverage | null>(null)
  const [assetSystemAccountability, setAssetSystemAccountability] =
    useState<AssetSystemAccountability | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [details, setDetails] = useState<Record<string, any>>({})
  const [activeScan, setActiveScan] = useState<ScanStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newAsset, setNewAsset] = useState({ name: '', ip: '', hostname: '', url: '', type: '', description: '', environment: '' })
  const [editId, setEditId] = useState<string | null>(null)
  const [editAsset, setEditAsset] = useState<Asset | null>(null)
  const [editOpen, setEditOpen] = useState(false) // kept for compatibility, not used for modal now
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [scannerType, setScannerType] = useState<'openvas'|'cve'>('openvas')
  const [scanPanelOpen, setScanPanelOpen] = useState(false)
  const [manualTargets, setManualTargets] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [starting, setStarting] = useState(false)
  const redirectToLoginIf401 = (res: Response) => {
    if (res && res.status === 401) {
      try { localStorage.removeItem('auth_token'); sessionStorage.removeItem('authToken') } catch {}
      window.location.href = '/login'
      return true
    }
    return false
  }

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState<'critical' | 'high' | 'medium' | 'total' | 'name'>('critical')
  const [severityFilter, setSeverityFilter] = useState<Set<'critical' | 'high' | 'medium' | 'low'>>(new Set())
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set())
  const [criticalityFilter, setCriticalityFilter] = useState<Set<string>>(new Set())
  const [ownerFilter, setOwnerFilter] = useState<Set<string>>(new Set())
  const [environmentFilter, setEnvironmentFilter] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<AssetsWorkspaceTab>('information-systems')
  const [hasVulnerabilitiesOnly, setHasVulnerabilitiesOnly] = useState(false)
  const [archivedSystemsParam, setArchivedSystemsParam] = useState(false)
  const [securityControlFilters, setSecurityControlFilters] =
    useState<SecurityControlInventoryFilters>(() =>
      parseSecurityControlInventoryFilters(new URLSearchParams(window.location.search)),
    )
  const [accountabilityFilters, setAccountabilityFilters] =
    useState<AssetSystemAccountabilityFilters>(() =>
      parseAssetSystemAccountabilityFilters(new URLSearchParams(window.location.search)),
    )
  const [coverageRefreshKey, setCoverageRefreshKey] = useState(0)

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'inventory' || tab === 'information-systems' || tab === 'data-map' || tab === 'risk-compliance') {
      setActiveTab(tab)
    }
    const hasVuln =
      searchParams.get('hasVulnerabilities') === 'true' ||
      searchParams.get('hasVulnerabilities') === '1' ||
      searchParams.get('hasCurrentVulnerabilities') === 'true'
    setHasVulnerabilitiesOnly(hasVuln)
    const archived =
      searchParams.get('archived') === 'true' ||
      searchParams.get('archived') === '1' ||
      searchParams.get('showArchived') === 'true'
    setArchivedSystemsParam(archived)
    setSecurityControlFilters(parseSecurityControlInventoryFilters(searchParams))
    setAccountabilityFilters(parseAssetSystemAccountabilityFilters(searchParams))
  }, [searchParams])

  useEffect(() => {
    const securityActive = hasActiveSecurityControlFilters(securityControlFilters)
    const accountabilityActive =
      activeTab === 'inventory' && hasActiveAccountabilityFilters(accountabilityFilters)
    if (!securityActive && !accountabilityActive) return
    if (activeTab !== 'inventory') return
    const t = window.setTimeout(() => {
      const el =
        document.getElementById('assets-security-filter-results') ||
        document.getElementById('assets-inventory-results')
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 80)
    return () => window.clearTimeout(t)
  }, [securityControlFilters, accountabilityFilters, activeTab])

  const handleWorkspaceTabChange = (tab: AssetsWorkspaceTab) => {
    setActiveTab(tab)
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: false })
  }

  const clearOneSecurityFilter = (
    key:
      | 'securityControl'
      | 'controlStatus'
      | 'securityCoverage'
      | 'verificationState'
      | 'sourceQuality'
      | 'showSecurityControls',
  ) => {
    const next = removeSecurityControlParam(searchParams, key)
    next.set('tab', 'inventory')
    setSearchParams(next, { replace: false })
  }

  const clearAllSecurityFilters = () => {
    const next = clearSecurityControlParams(searchParams)
    next.set('tab', 'inventory')
    setSearchParams(next, { replace: false })
  }

  const clearOneAccountabilityFilter = (key: 'owner' | 'informationSystem') => {
    const next = removeAccountabilityParam(searchParams, key)
    next.set('tab', activeTab === 'information-systems' ? 'information-systems' : 'inventory')
    setSearchParams(next, { replace: false })
  }

  const clearAllAccountabilityFilters = () => {
    const next = clearAccountabilityParams(searchParams)
    next.set('tab', activeTab === 'information-systems' ? 'information-systems' : 'inventory')
    setSearchParams(next, { replace: false })
  }

  const applySecurityInventoryFilter = (opts: BuildInventoryUrlOpts) => {
    const next = buildSecurityControlSearchParams(opts)
    // Preserve non-security inventory query params (e.g. hasVulnerabilities, owner)
    for (const [k, v] of searchParams.entries()) {
      if (
        k === 'securityControl' ||
        k === 'controlStatus' ||
        k === 'securityCoverage' ||
        k === 'verificationState' ||
        k === 'sourceQuality' ||
        k === 'showSecurityControls' ||
        k === 'tab'
      ) {
        continue
      }
      if (!next.has(k)) next.set(k, v)
    }
    next.set('tab', 'inventory')
    setActiveTab('inventory')
    setSearchParams(next, { replace: false })
    window.setTimeout(() => {
      const el =
        document.getElementById('assets-security-filter-results') ||
        document.getElementById('assets-inventory-results')
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  const applyAccountabilityFilter = (opts: AccountabilityHrefOpts) => {
    const tab = opts.tab || 'inventory'
    const built = buildAccountabilitySearchParams(opts)
    const next = new URLSearchParams()
    next.set('tab', tab)
    for (const [k, v] of built.entries()) {
      if (k !== 'tab') next.set(k, v)
    }
    // Preserve unrelated params (org, hasVulnerabilities) but drop the other accountability axis
    // when switching tabs / applying a focused filter from the card.
    for (const [k, v] of searchParams.entries()) {
      if (
        k === 'tab' ||
        k === 'owner' ||
        k === 'informationSystem' ||
        k === 'securityControl' ||
        k === 'controlStatus' ||
        k === 'securityCoverage' ||
        k === 'verificationState' ||
        k === 'sourceQuality' ||
        k === 'showSecurityControls'
      ) {
        continue
      }
      if (!next.has(k)) next.set(k, v)
    }
    setActiveTab(tab)
    setSearchParams(next, { replace: false })
    window.setTimeout(() => {
      const id =
        tab === 'information-systems'
          ? 'assets-information-systems'
          : 'assets-security-filter-results'
      const el =
        document.getElementById(id) ||
        document.getElementById('assets-inventory-results')
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }
  const [focusSystemId, setFocusSystemId] = useState<string | null>(null)
  const [passportSection, setPassportSection] = useState<PassportSection>('overview')
  const [informationSystemFilter, setInformationSystemFilter] = useState<string>('')
  const [filteredAssetIds, setFilteredAssetIds] = useState<Set<string> | null>(null)
  const [categoryView, setCategoryView] = useState<AssetCategoryView | ''>('')

  // Treat as having organization context to avoid hiding UI; backend may infer org from token
  const hasOrganizations = true
  const organizationId = getActiveOrgId()
  const organizationParam = organizationId
  const { posture: vulnerabilityPosture, trend: vulnerabilityTrend, loading: postureLoading } =
    useCurrentVulnerabilityPosture(Boolean(token))

  // Refresh user at most once when org is missing to avoid infinite loop (refreshUser -> setUser -> effect -> refreshUser)
  const refreshedOrgOnceRef = React.useRef(false)
  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }
    const hasOrg = (Array.isArray(user?.organizations) && user.organizations.length > 0) || (user as any)?.organizationId
    if (hasOrg) {
      fetchAssets()
      checkActiveScan()
      return
    }
    if (refreshedOrgOnceRef.current) {
      fetchAssets()
      checkActiveScan()
      return
    }
    refreshedOrgOnceRef.current = true
    refreshUser().finally(() => {
      fetchAssets()
      checkActiveScan()
    })
  }, [user?.id, organizationId])

  useEffect(() => {
    if (!informationSystemFilter || !token) {
      setFilteredAssetIds(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const detail = await fetchInformationSystem(token, informationSystemFilter, organizationId)
        const ids = new Set((detail.assetLinks || []).map((l) => l.assetId))
        if (!cancelled) setFilteredAssetIds(ids)
      } catch {
        if (!cancelled) setFilteredAssetIds(null)
      }
    })()
    return () => { cancelled = true }
  }, [informationSystemFilter, token, organizationId])

  const fetchAssets = async () => {
    setLoading(true)
    setError(null)
    try {
      const url = organizationParam
        ? API_ENDPOINTS.ORG_ASSETS_LIST(organizationParam)
        : API_ENDPOINTS.ASSETS
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (redirectToLoginIf401(response)) return

      if (!response.ok) {
        const dto = await safeJson(response)
        const msg = (dto && (dto.error || dto.message)) ? String(dto.error || dto.message) : t('assets.error.fetchFailed')
        throw new Error(msg)
      }

      const data = await safeJson(response)
      const rows = Array.isArray(data?.data) ? data.data : []
      if (data?.securityProtectionCoverage) {
        setSecurityProtectionCoverage(data.securityProtectionCoverage as SecurityProtectionCoverage)
      }
      if (data?.assetSystemAccountability) {
        setAssetSystemAccountability(data.assetSystemAccountability as AssetSystemAccountability)
      }
      const emptyVulns = { critical: 0, high: 0, medium: 0, low: 0, total: 0 }
      setAssets(
        rows.map((row: Record<string, unknown>) => {
          const rawDetails = row.details
          const detailsObj =
            rawDetails && typeof rawDetails === 'object'
              ? (rawDetails as Record<string, unknown>)
              : typeof rawDetails === 'string'
                ? (() => {
                    try {
                      return JSON.parse(rawDetails) as Record<string, unknown>
                    } catch {
                      return {}
                    }
                  })()
                : {}
          const ownerObj =
            row.owner && typeof row.owner === 'object'
              ? (row.owner as { id?: string; name?: string; department?: string; email?: string })
              : null
          const base = {
            ...row,
            id: String(row.id ?? row.assetId ?? ''),
            assetId: String(row.assetId ?? row.id ?? ''),
            details: detailsObj,
            owner: ownerObj
              ? {
                  id: String(ownerObj.id || ''),
                  name: String(ownerObj.name || ''),
                  department: ownerObj.department,
                  email: ownerObj.email,
                }
              : undefined,
            hasOwner: Boolean(row.hasOwner ?? ownerObj),
            linkedToInformationSystem: Boolean(row.linkedToInformationSystem),
            ownerId: row.ownerId != null ? String(row.ownerId) : null,
            vulnerabilities:
              row.vulnerabilities && typeof row.vulnerabilities === 'object'
                ? row.vulnerabilities
                : emptyVulns,
            securityControlCoverage: row.securityControlCoverage,
            unknownControlsCount: Number(row.unknownControlsCount ?? 0),
            disabledControlsCount: Number(row.disabledControlsCount ?? 0),
            verifiedControlsCount: Number(row.verifiedControlsCount ?? 0),
            knownControlsCount: Number(row.knownControlsCount ?? 0),
            lastSecurityControlObservationAt: row.lastSecurityControlObservationAt
              ? String(row.lastSecurityControlObservationAt)
              : null,
            securityCoverageStatus: row.securityCoverageStatus
              ? String(row.securityCoverageStatus)
              : undefined,
          }
          const scanHost = resolveAssetScanHost(
            {
              ipAddress: String(row.ipAddress ?? ''),
              name: String(row.name ?? ''),
              details: detailsObj,
            },
            detailsObj,
          )
          return {
            ...base,
            ipAddress: scanHost || String(row.ipAddress ?? ''),
          } as Asset
        }),
      )
    } catch (err: any) {
      console.error('Assets page - Fetch error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const refreshCharts = async () => {
    // reload assets to recompute local summaries
    await fetchAssets()
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organizationParam) { setError('Organization not found'); return }
    if (!(newAsset.name?.trim() || newAsset.ip?.trim() || newAsset.hostname?.trim() || newAsset.url?.trim())) {
      setError('Provide Name/IP/Hostname/URL')
      return
    }
    setCreating(true)
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ORG_ASSETS_CREATE(organizationParam), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAsset.name,
          type: newAsset.type || 'OTHER',
          environment: newAsset.environment || 'PRODUCTION',
          ip: newAsset.ip,
          hostname: newAsset.hostname,
          url: newAsset.url,
          description: newAsset.description,
        })
      })
      if (redirectToLoginIf401(res)) return
      const dto = await safeJson(res)
      if (!res.ok) throw new Error(dto?.message || 'Failed to create asset')
      setAssets(dto.data || [])
      setNewAsset({ name: '', ip: '', hostname: '', url: '', type: '', description: '', environment: '' })
      await refreshCharts()
    } catch (err:any) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const submitEdit = async () => {
    try { console.log('[ASSET EDIT] submit', { editId, editAsset }) } catch {}
    if (!organizationParam || !editId) return
    try {
      setEditLoading(true)
      const res = await fetch(API_ENDPOINTS.ORG_ASSETS_UPDATE(organizationParam, editId), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editAsset)
      })
      if (redirectToLoginIf401(res)) return
      const dto = await safeJson(res)
      if (!res.ok) throw new Error(dto?.message || 'Failed to update asset')
      await fetchAssets()
      setCoverageRefreshKey((k) => k + 1)
      setEditId(null)
      setEditAsset({})
      setEditOpen(false)
      setEditError(null)
    } catch (e:any) {
      setEditError(e.message)
    } finally {
      setEditLoading(false)
    }
  }

  const openEditModal = async (asset: Asset) => {
    try {
      try { console.log('[ASSET EDIT] open modal', { id: asset.id }) } catch {}
      setEditError(null)
      setEditLoading(true)
      setEditId(asset.id)
      setEditOpen(true)
      
      // Fetch full asset details
      const detailsUrl = organizationParam
        ? API_ENDPOINTS.ORG_ASSET_DETAILS(organizationParam, asset.id)
        : `/api/assets/${asset.id}/details`
      const res = await fetch(detailsUrl, { headers: { 'Authorization': `Bearer ${token}` } })
      if (redirectToLoginIf401(res)) { return }
      
      if (res.ok) {
        const det = await safeJson(res)
        const baseDetails =
          asset.details && typeof asset.details === 'object'
            ? (asset.details as Record<string, unknown>)
            : {}
        const mergedDetails = {
          ...baseDetails,
          ip: det?.ip ?? baseDetails.ip ?? '',
          hostname: det?.hostname ?? baseDetails.hostname ?? '',
          url: det?.url ?? baseDetails.url ?? '',
          description: det?.description ?? baseDetails.description ?? '',
        }
        const tagList = Array.isArray(det?.tags)
          ? det.tags
          : Array.isArray(mergedDetails.tags)
            ? (mergedDetails.tags as string[])
            : asset.tags || []
        const fullAsset: Asset = {
          ...asset,
          ...det,
          owner: det?.owner || asset.owner,
          tags: tagList,
          posture: det?.posture || asset.posture,
          details: mergedDetails,
          criticality: det?.criticality || asset.criticality,
          riskScore: det?.riskScore ?? asset.riskScore,
          complianceScore: det?.complianceScore ?? asset.complianceScore,
        }
        setEditAsset(fullAsset)
      } else {
        const d =
          asset.details && typeof asset.details === 'object'
            ? (asset.details as Record<string, unknown>)
            : {}
        const ownerUid = d.ownerUserId ?? d.ownerId
        setEditAsset({
          ...asset,
          details: {
            ip: String(d.ip ?? ''),
            hostname: String(d.hostname ?? ''),
            url: String(d.url ?? ''),
            description: String(d.description ?? ''),
            ...d,
          },
          owner:
            asset.owner ||
            (ownerUid
              ? {
                  id: String(ownerUid),
                  username: String(d.ownerName ?? ''),
                }
              : null),
          tags: Array.isArray(d.tags) ? (d.tags as string[]) : asset.tags || [],
          criticality: asset.criticality,
          riskScore: asset.riskScore,
          complianceScore: asset.complianceScore,
        })
      }
    } catch (e:any) {
      setEditError(e.message)
      const d =
        asset.details && typeof asset.details === 'object'
          ? (asset.details as Record<string, unknown>)
          : {}
      setEditAsset({
        ...asset,
        details: d,
        owner: asset.owner,
        tags: asset.tags || [],
      })
      setEditOpen(true)
    } finally {
      setEditLoading(false)
    }
  }

  const closeEditModal = () => {
    setEditId(null)
    setEditAsset(null)
    setEditOpen(false)
    setEditError(null)
  }
  
  const handleAssetUpdated = async () => {
    await fetchAssets()
    setCoverageRefreshKey((k) => k + 1)
    setEditId(null)
    setEditAsset(null)
    setEditOpen(false)
    setEditError(null)
  }

  const handleDelete = async (assetId: string) => {
    if (!organizationParam) { setError('Organization not found'); return }
    if (!window.confirm(t('assets.inventory.deleteConfirm', 'Delete this asset from the organization inventory?'))) {
      return
    }
    try {
      const res = await fetch(API_ENDPOINTS.ORG_ASSETS_DELETE(organizationParam, assetId), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (redirectToLoginIf401(res)) return
      const dto = await safeJson(res)
      if (!res.ok) throw new Error(dto?.message || 'Failed to delete asset')
      setAssets(dto.data || [])
      await refreshCharts()
    } catch (err:any) {
      setError(err.message)
    }
  }

  const getRiskLevelColor = (riskLevel: string | undefined | null) => {
    if (typeof riskLevel !== 'string') return 'text-gray-700 bg-gray-50'
    const lvl = riskLevel.toLowerCase()
    switch (lvl) {
      case 'critical': return 'text-red-800 bg-red-100'
      case 'high': return 'text-red-700 bg-red-50'
      case 'medium': return 'text-yellow-700 bg-yellow-50'
      case 'low': return 'text-green-700 bg-green-50'
      default: return 'text-gray-700 bg-gray-50'
    }
  }

  const toggleExpand = async (assetId: string) => {
    setExpandedId(prev => (prev === assetId ? null : assetId))
    if (!details[assetId]) {
      try {
        // Prefer org-scoped endpoint to avoid proxy 404; fallback to legacy
        const url = organizationParam
          ? API_ENDPOINTS.ORG_ASSET_DETAILS(organizationParam, assetId)
          : `/api/assets/${assetId}/details`
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
        if (redirectToLoginIf401(res)) { return }
        const data = res.ok ? await safeJson(res) : { asset: null, vulnerabilities: [] }
        setDetails(prev => ({ ...prev, [assetId]: data }))
      } catch (e) {
        // ignore
      }
    }
  }

  const getRiskLevelIcon = (riskLevel: string | undefined | null) => {
    if (typeof riskLevel !== 'string') return <Shield className="h-4 w-4 text-gray-500" />
    const lvl = riskLevel.toLowerCase()
    switch (lvl) {
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-600" />
      case 'high': return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'medium': return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      case 'low': return <CheckCircle className="h-4 w-4 text-green-500" />
      default: return <Shield className="h-4 w-4 text-gray-500" />
    }
  }

  const getVulnerabilityColor = (level: string | undefined | null) => {
    if (typeof level !== 'string') return '#6B7280'
    const lvl = level.toLowerCase()
    switch (lvl) {
      case 'critical': return '#8B0000'
      case 'high': return '#DC2626'
      case 'medium': return '#3B82F6'
      case 'low': return '#10B981'
      default: return '#6B7280'
    }
  }

  const checkActiveScan = async () => {
    try {
      const history = await scanService.getScanHistory()
      if (history.length > 0) {
        const latestScan = history[0]
        setActiveScan(latestScan)
        
        // Update asset information based on scan results
        if (latestScan.status === 'completed') {
          const report = await scanService.getScanReport(latestScan.scanId)
          if (report) {
            updateAssetsFromScan([report])
          }
        }
      }
    } catch (error) {
      console.error('Failed to check active scan:', error)
    }
  }

  const updateAssetsFromScan = (reports: any[]) => {
    setAssets(prevAssets => 
      prevAssets.map(asset => {
        // Find reports for this asset
        const assetReports = reports.filter(report => 
          report.host === asset.ipAddress || report.host === asset.name
        )
        
        if (assetReports.length > 0) {
          // Calculate new risk level based on scan results
          const criticalCount = assetReports.filter(r => r.riskLevel === 'Critical').length
          const highCount = assetReports.filter(r => r.riskLevel === 'High').length
          const mediumCount = assetReports.filter(r => r.riskLevel === 'Medium').length
          const lowCount = assetReports.filter(r => r.riskLevel === 'Low').length
          
          let newRiskLevel = 'Low'
          let newCompliance = 100
          
          if (criticalCount > 0) {
            newRiskLevel = 'Critical'
            newCompliance = 25
          } else if (highCount > 0) {
            newRiskLevel = 'High'
            newCompliance = 50
          } else if (mediumCount > 0) {
            newRiskLevel = 'Medium'
            newCompliance = 75
          }
          
          return {
            ...asset,
            riskLevel: newRiskLevel,
            complianceScore: newCompliance,
            vulnerabilities: {
              critical: criticalCount,
              high: highCount,
              medium: mediumCount,
              low: lowCount,
              total: criticalCount + highCount + mediumCount + lowCount
            },
            lastAssessment: new Date().toLocaleDateString()
          }
        }
        
        return asset
      })
    )
  }

  // Helpers for sorting & filtering
  const getAvailableTypes = () => {
    const types = Array.from(new Set((assets || []).map(a => a.type).filter(Boolean)))
    return types
  }

  const assetMatchesSeverity = (asset: Asset): boolean => {
    if (!severityFilter || !(severityFilter instanceof Set) || severityFilter.size === 0) return true
    if (severityFilter.has('critical') && asset.vulnerabilities.critical > 0) return true
    if (severityFilter.has('high') && asset.vulnerabilities.high > 0) return true
    if (severityFilter.has('medium') && asset.vulnerabilities.medium > 0) return true
    if (severityFilter.has('low') && asset.vulnerabilities.low > 0) return true
    return false
  }

  const assetMatchesType = (asset: Asset): boolean => {
    if (!typeFilter || !(typeFilter instanceof Set) || typeFilter.size === 0) return true
    return typeFilter.has(String(asset.type || ''))
  }

  const assetMatchesCriticality = (asset: Asset): boolean => {
    if (!criticalityFilter || !(criticalityFilter instanceof Set) || criticalityFilter.size === 0) return true
    return criticalityFilter.has(String(asset.criticality || 'Unknown'))
  }

  const assetMatchesOwner = (asset: Asset): boolean => {
    if (!ownerFilter || !(ownerFilter instanceof Set) || ownerFilter.size === 0) return true
    if (!asset.owner) return false
    return ownerFilter.has(asset.owner.id)
  }

  const assetMatchesEnvironment = (asset: Asset): boolean => {
    if (!environmentFilter || !(environmentFilter instanceof Set) || environmentFilter.size === 0) return true
    return environmentFilter.has(String(asset.environment || ''))
  }

  const assetMatchesSearch = (asset: Asset): boolean => {
    if (!searchQuery || searchQuery.trim() === '') return true
    const query = searchQuery.toLowerCase().trim()
    return (
      asset.name.toLowerCase().includes(query) ||
      asset.ipAddress?.toLowerCase().includes(query) ||
      asset.type?.toLowerCase().includes(query) ||
      asset.environment?.toLowerCase().includes(query)
    )
  }

  const assetMatchesInformationSystem = (asset: Asset): boolean => {
    if (!informationSystemFilter || !filteredAssetIds) return true
    const id = asset.id || asset.assetId
    return filteredAssetIds.has(id)
  }

  const assetMatchesCategory = (asset: Asset): boolean => {
    if (!categoryView) return true
    return assetMatchesCategoryView(asset.type, categoryView)
  }

  const getAvailableCriticalities = () => {
    const crits = Array.from(new Set((assets || []).map(a => a.criticality || 'Unknown').filter(Boolean)))
    return crits
  }

  const getAvailableOwners = () => {
    const owners = new Map<string, { id: string; name: string }>()
    assets.forEach(a => {
      if (a.owner) {
        owners.set(a.owner.id, a.owner)
      }
    })
    return Array.from(owners.values())
  }

  const getAvailableEnvironments = () => {
    const envs = Array.from(new Set((assets || []).map(a => a.environment).filter(Boolean)))
    return envs
  }

  const compareAssets = (a: Asset, b: Asset): number => {
    const emptyVulns = { critical: 0, high: 0, medium: 0, low: 0, total: 0 }
    const av = a.vulnerabilities ?? emptyVulns
    const bv = b.vulnerabilities ?? emptyVulns
    // Default/tie-break comparator: critical desc, then high, medium, low
    const descChain = [
      () => (bv.critical - av.critical),
      () => (bv.high - av.high),
      () => (bv.medium - av.medium),
      () => (bv.low - av.low),
    ]

    if (sortBy === 'critical') {
      for (const cmp of descChain) { const d = cmp(); if (d !== 0) return d }
      return a.name.localeCompare(b.name)
    }
    if (sortBy === 'high') return (bv.high - av.high) || (bv.critical - av.critical) || (bv.medium - av.medium) || (bv.low - av.low) || a.name.localeCompare(b.name)
    if (sortBy === 'medium') return (bv.medium - av.medium) || (bv.high - av.high) || (bv.critical - av.critical) || (bv.low - av.low) || a.name.localeCompare(b.name)
    if (sortBy === 'total') return (bv.total - av.total) || (bv.critical - av.critical) || (bv.high - av.high) || (bv.medium - av.medium) || (bv.low - av.low) || a.name.localeCompare(b.name)
    if (sortBy === 'name') return a.name.localeCompare(b.name)
    return 0
  }

  const assetMatchesHasVulnerabilities = (asset: Asset): boolean => {
    if (!hasVulnerabilitiesOnly) return true
    return (asset.vulnerabilities?.total ?? 0) > 0
  }

  const assetMatchesSecurityControl = (asset: Asset): boolean => {
    return assetMatchesSecurityControlFilters(asset, securityControlFilters)
  }

  const assetMatchesAccountability = (asset: Asset): boolean => {
    return assetMatchesAccountabilityFilters(asset, accountabilityFilters)
  }

  const visibleAssets = (assets || [])
    .filter(assetMatchesType)
    .filter(assetMatchesSeverity)
    .filter(assetMatchesCriticality)
    .filter(assetMatchesOwner)
    .filter(assetMatchesEnvironment)
    .filter(assetMatchesSearch)
    .filter(assetMatchesInformationSystem)
    .filter(assetMatchesCategory)
    .filter(assetMatchesHasVulnerabilities)
    .filter(assetMatchesSecurityControl)
    .filter(assetMatchesAccountability)
    .sort(compareAssets)

  const inventoryFilterActive =
    hasActiveSecurityControlFilters(securityControlFilters) ||
    (activeTab === 'inventory' && hasActiveAccountabilityFilters(accountabilityFilters))

  const CONTROL_CHIP_LABELS: Record<string, { key: string; fallback: string }> = {
    EDR: { key: 'assets.securityControls.controls.edr', fallback: 'EDR / XDR' },
    HOST_FIREWALL: { key: 'assets.securityControls.controls.hostFirewall', fallback: 'Host Firewall' },
    DISK_ENCRYPTION: { key: 'assets.securityControls.controls.encryption', fallback: 'Encryption' },
    PATCH_MANAGEMENT: { key: 'assets.securityControls.controls.patch', fallback: 'Patch Management' },
    BACKUP_RECOVERY: { key: 'assets.securityControls.controls.backup', fallback: 'Backup' },
    SIEM_INTEGRATION: { key: 'assets.securityControls.controls.siem', fallback: 'Log / SIEM Coverage' },
  }

  const STATUS_CHIP_LABELS: Record<string, { key: string; fallback: string }> = {
    ENABLED: { key: 'assets.securityControls.enabled', fallback: 'Enabled' },
    DISABLED: { key: 'assets.securityControls.disabled', fallback: 'Disabled' },
    PARTIAL: { key: 'assets.securityControls.partial', fallback: 'Partial' },
    UNKNOWN: { key: 'assets.securityControls.unknown', fallback: 'Unknown' },
  }

  const COVERAGE_CHIP_LABELS: Record<string, { key: string; fallback: string }> = {
    known: { key: 'assets.securityControls.knownCoverage', fallback: 'Known' },
    verified: { key: 'assets.securityControls.verifiedCoverage', fallback: 'Verified' },
    unknown: { key: 'assets.securityControls.unknownPosture', fallback: 'Unknown posture' },
    gap: { key: 'assets.securityControls.requiresAttention', fallback: 'Requires attention' },
    all: { key: 'assets.securityControls.viewAssetCoverage', fallback: 'Asset security coverage' },
  }

  const VERIFICATION_CHIP_LABELS: Record<string, { key: string; fallback: string }> = {
    VERIFIED: { key: 'assets.securityControls.verified', fallback: 'Verified' },
    SELF_REPORTED: { key: 'assets.securityControls.selfReported', fallback: 'Self-reported' },
    STALE: { key: 'assets.securityControls.stale', fallback: 'Stale' },
    UNKNOWN: { key: 'assets.securityControls.unknown', fallback: 'Unknown' },
    DISCOVERED: { key: 'assets.securityControls.discovered', fallback: 'Discovered' },
    MANUALLY_VERIFIED: { key: 'assets.securityControls.manuallyVerified', fallback: 'Manually verified' },
  }

  const securityFilterChips = (() => {
    if (!hasActiveSecurityControlFilters(securityControlFilters)) return null
    const chips: Array<{ key: string; param: Parameters<typeof clearOneSecurityFilter>[0]; label: string }> = []
    if (securityControlFilters.securityControl) {
      const meta = CONTROL_CHIP_LABELS[securityControlFilters.securityControl]
      chips.push({
        key: 'control',
        param: 'securityControl',
        label: `${t('assets.securityControls.control', 'Security control')}: ${
          meta ? t(meta.key, meta.fallback) : securityControlFilters.securityControl
        }`,
      })
    }
    if (securityControlFilters.controlStatus) {
      const meta = STATUS_CHIP_LABELS[securityControlFilters.controlStatus]
      chips.push({
        key: 'status',
        param: 'controlStatus',
        label: `${t('assets.securityControls.status', 'Status')}: ${
          meta ? t(meta.key, meta.fallback) : securityControlFilters.controlStatus
        }`,
      })
    }
    if (securityControlFilters.securityCoverage) {
      const meta = COVERAGE_CHIP_LABELS[securityControlFilters.securityCoverage]
      chips.push({
        key: 'coverage',
        param: 'securityCoverage',
        label: meta
          ? t(meta.key, meta.fallback)
          : securityControlFilters.securityCoverage,
      })
    }
    if (securityControlFilters.verificationState) {
      const meta = VERIFICATION_CHIP_LABELS[securityControlFilters.verificationState]
      chips.push({
        key: 'verification',
        param: 'verificationState',
        label: meta
          ? t(meta.key, meta.fallback)
          : securityControlFilters.verificationState,
      })
    }
    if (securityControlFilters.sourceQuality) {
      const meta = VERIFICATION_CHIP_LABELS[securityControlFilters.sourceQuality]
      chips.push({
        key: 'quality',
        param: 'sourceQuality',
        label: `${t('assets.securityControls.sourceQuality', 'Source quality')}: ${
          meta ? t(meta.key, meta.fallback) : securityControlFilters.sourceQuality
        }`,
      })
    }
    if (securityControlFilters.showSecurityControls && !securityControlFilters.securityCoverage) {
      chips.push({
        key: 'show',
        param: 'showSecurityControls',
        label: t('assets.securityControls.viewAssetCoverage', 'Asset security coverage'),
      })
    }
    return (
      <div
        className={assetsPageStyles.securityFilterBar}
        data-testid="security-control-filter-chips"
        role="region"
        aria-label={t('assets.securityControls.activeFilters', 'Active security control filters')}
      >
        <div className={assetsPageStyles.securityFilterChips}>
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={assetsPageStyles.securityFilterChip}
              onClick={() => clearOneSecurityFilter(chip.param)}
              aria-label={`${t('assets.securityControls.clearFilter', 'Clear filter')}: ${chip.label}`}
            >
              <span>{chip.label}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
        <Button type="button" color="outline" size="small" onClick={clearAllSecurityFilters}>
          {t('assets.securityControls.clearAllFilters', 'Clear all')}
        </Button>
        <span className={assetsPageStyles.securityFilterCount}>
          {visibleAssets.length}{' '}
          {t('assets.securityControls.matchingAssets', 'matching assets')}
        </span>
      </div>
    )
  })()

  const accountabilityFilterChips = (() => {
    if (activeTab !== 'inventory' && activeTab !== 'information-systems') return null
    if (!hasActiveAccountabilityFilters(accountabilityFilters)) return null
    // On IS tab only owner filter applies; informationSystem is inventory-only
    if (activeTab === 'information-systems' && !accountabilityFilters.owner) return null
    const chips: Array<{ key: string; param: 'owner' | 'informationSystem'; label: string }> = []
    if (accountabilityFilters.owner) {
      chips.push({
        key: 'owner',
        param: 'owner',
        label: `${t('assets.accountability.ownerFilter', 'Owner')}: ${
          accountabilityFilters.owner === 'assigned'
            ? t('assets.accountability.assigned', 'Assigned')
            : t('assets.accountability.unassigned', 'Unassigned')
        }`,
      })
    }
    if (activeTab === 'inventory' && accountabilityFilters.informationSystem) {
      chips.push({
        key: 'is',
        param: 'informationSystem',
        label: `${t('assets.accountability.informationSystemFilter', 'Information System')}: ${
          accountabilityFilters.informationSystem === 'linked'
            ? t('assets.accountability.linked', 'Linked')
            : t('assets.accountability.unlinked', 'Unlinked')
        }`,
      })
    }
    if (!chips.length) return null
    return (
      <div
        className={assetsPageStyles.securityFilterBar}
        data-testid="accountability-filter-chips"
        role="region"
        aria-label={t('assets.accountability.activeFilters', 'Active accountability filters')}
      >
        <div className={assetsPageStyles.securityFilterChips}>
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={assetsPageStyles.securityFilterChip}
              onClick={() => clearOneAccountabilityFilter(chip.param)}
              aria-label={`${t('assets.accountability.clearFilter', 'Clear filter')}: ${chip.label}`}
            >
              <span>{chip.label}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
        <Button type="button" color="outline" size="small" onClick={clearAllAccountabilityFilters}>
          {t('assets.accountability.clearAllFilters', 'Clear all')}
        </Button>
        {activeTab === 'inventory' ? (
          <span className={assetsPageStyles.securityFilterCount}>
            {visibleAssets.length}{' '}
            {t('assets.accountability.matchingAssets', 'matching assets')}
          </span>
        ) : null}
      </div>
    )
  })()

  // Calculate statistics for inventory posture panel and legacy AssetsOverview
  const assetsStats = useMemo((): AssetOverviewStats => {
    const total = vulnerabilityPosture?.assetsTotal ?? assets.length
    const visible = visibleAssets.length
    const unknown = visibleAssets.filter((a) => !a.type || a.type === 'UNKNOWN' || a.type === '').length
    const avgRisk =
      visibleAssets.length > 0
        ? visibleAssets.reduce(
            (sum, a) => sum + (a.riskScore || (a.complianceScore ? 100 - a.complianceScore : 0)),
            0,
          ) / visibleAssets.length
        : 0
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    const newAssets = assets.filter((a) => {
      const created = a.createdAt ? new Date(a.createdAt) : null
      return created && created >= sevenDaysAgo
    }).length
    const avgCompliance =
      visibleAssets.length > 0
        ? visibleAssets.reduce((sum, a) => sum + (a.complianceScore || 0), 0) / visibleAssets.length
        : 0
    const edrCoverage = 0

    if (vulnerabilityPosture) {
      return {
        totalAssets: total,
        visibleAssets: visible,
        unknownAssets: unknown,
        assetsWithVulnerabilities: vulnerabilityPosture.assetsWithVulnerabilities,
        totalVulnerabilities:
          vulnerabilityPosture.currentUniqueFindings ?? vulnerabilityPosture.total,
        currentUniqueFindings:
          vulnerabilityPosture.currentUniqueFindings ??
          vulnerabilityPosture.openFindings ??
          vulnerabilityPosture.total,
        currentAffectedInstances:
          vulnerabilityPosture.currentAffectedInstances ?? vulnerabilityPosture.total,
        criticalVulnerabilities: vulnerabilityPosture.critical,
        highVulnerabilities: vulnerabilityPosture.high,
        mediumVulnerabilities: vulnerabilityPosture.medium,
        lowVulnerabilities: vulnerabilityPosture.low,
        infoVulnerabilities: vulnerabilityPosture.info ?? 0,
        avgRiskScore: avgRisk,
        newAssetsLast7Days: newAssets,
        complianceAvg: avgCompliance,
        edrCoverage,
      }
    }

    const vulnFromAssets = visibleAssets.reduce(
      (acc, a) => {
        acc.total += a.vulnerabilities?.total ?? 0
        acc.critical += a.vulnerabilities?.critical ?? 0
        acc.high += a.vulnerabilities?.high ?? 0
        acc.medium += a.vulnerabilities?.medium ?? 0
        acc.low += a.vulnerabilities?.low ?? 0
        if ((a.vulnerabilities?.total ?? 0) > 0) acc.assetsWithVulnerabilities += 1
        return acc
      },
      { total: 0, critical: 0, high: 0, medium: 0, low: 0, assetsWithVulnerabilities: 0 },
    )

    return {
      totalAssets: total,
      visibleAssets: visible,
      unknownAssets: unknown,
      assetsWithVulnerabilities: vulnFromAssets.assetsWithVulnerabilities,
      totalVulnerabilities: vulnFromAssets.total,
      criticalVulnerabilities: vulnFromAssets.critical,
      highVulnerabilities: vulnFromAssets.high,
      mediumVulnerabilities: vulnFromAssets.medium,
      lowVulnerabilities: vulnFromAssets.low,
      avgRiskScore: avgRisk,
      newAssetsLast7Days: newAssets,
      complianceAvg: avgCompliance,
      edrCoverage,
    }
  }, [assets, visibleAssets, vulnerabilityPosture])

  if (loading) {
    return (
      <PageLayout pageScope="assets-page" title={t('assets.pageTitle', 'Assets')}>
        <p className="text-sm text-gray-600">{t('common.loading')}</p>
      </PageLayout>
    )
  }
  if (error) {
    return (
      <PageLayout pageScope="assets-page" title={t('assets.pageTitle', 'Assets')}>
        <p className="text-red-500">{t('common.error')}: {error}</p>
      </PageLayout>
    )
  }

  return (
    <>
    <PageLayout pageScope="assets-page" title={
        <span className={assetsHeaderStyles.titleRow}>
          <Server className={assetsHeaderStyles.titleIcon} aria-hidden />
          <span className={assetsHeaderStyles.titleLabel}>{t('assets.pageTitle', 'Assets')}</span>
        </span>
      }
      actions={
        hasOrganizations && activeScan ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
            {activeScan.status === 'running' && <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />}
            {activeScan.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
            {activeScan.status === 'failed' && <AlertTriangle className="h-4 w-4 text-red-600" />}
            <span className="text-sm font-medium text-blue-800">
              {activeScan.status === 'running' && `Scanning... ${activeScan.progress}%`}
              {activeScan.status === 'completed' && 'Scan Completed'}
              {activeScan.status === 'failed' && 'Scan Failed'}
            </span>
          </div>
        ) : undefined
      }
    >
      {hasOrganizations && (
        <AssetsWorkspace activeTab={activeTab} onTabChange={handleWorkspaceTabChange}>
          {activeTab === 'information-systems' && (
            <div id="assets-information-systems">
              {accountabilityFilterChips}
              <InformationSystemsTab
                focusSystemId={focusSystemId}
                passportSection={passportSection}
                initialShowArchived={archivedSystemsParam}
                ownerFilter={
                  activeTab === 'information-systems' ? accountabilityFilters.owner : null
                }
                onFocusHandled={() => {
                  setFocusSystemId(null)
                  setPassportSection('overview')
                }}
                assets={assets.map((a) => ({
                  id: a.id || a.assetId,
                  name: a.name,
                  type: a.type,
                  environment: a.environment,
                  criticality: a.criticality,
                  ipAddress: a.ipAddress,
                  hostname: (a as { hostname?: string }).hostname,
                  url: (a as { url?: string }).url,
                }))}
              />
            </div>
          )}
          {activeTab === 'data-map' && <DataMapTab />}
          {activeTab === 'risk-compliance' && (
            <RiskComplianceTab
              onOpenSystem={(id) => {
                setFocusSystemId(id)
                setPassportSection('overview')
                setActiveTab('information-systems')
              }}
              onOpenGraph={(id) => {
                setFocusSystemId(id)
                setPassportSection('graph')
                setActiveTab('information-systems')
              }}
            />
          )}
          {activeTab === 'inventory' && (
          <AssetInventoryTab>
          <CloudAssetProposalsCard />
        {featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
          <EnterpriseOperationalPage>
        <div className={assetsPageStyles.pageStack}>
          {!featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? <AssetsOverview stats={assetsStats} /> : null}
          <div className={assetsPageStyles.horizontalRow} aria-label="Asset actions">
            <div className={assetsPageStyles.actionCardSlot}>
              <AddAssetCard onAssetAdded={fetchAssets} />
            </div>
            <div className={assetsPageStyles.actionCardSlot}>
              <ActiveScansCard />
            </div>
            <div className={assetsPageStyles.actionCardSlot}>
              <OpenVASScanCard
                assets={visibleAssets.map((a) => {
                  const id = String(a.id || a.assetId)
                  const det = (details[id] || a.details || {}) as Record<string, unknown>
                  const scanHost = resolveAssetScanHost(a, det)
                  return {
                    id,
                    assetId: a.assetId,
                    name: a.name,
                    ipAddress: scanHost || a.ipAddress,
                    scanHost,
                    type: a.type,
                    environment: a.environment,
                    details: det,
                  }
                })}
                onScanStarted={() => {
                  window.location.reload()
                }}
              />
            </div>
          </div>

          <InformationSystemInventoryFilter
            value={informationSystemFilter}
            onChange={setInformationSystemFilter}
            organizationId={organizationParam}
            token={token}
          />

          <div id="assets-inventory-results">
          {securityFilterChips}
          {accountabilityFilterChips}
          <AssetInventoryExecutiveProvider
            overviewStats={assetsStats}
            assets={visibleAssets.map((a) => {
              const id = String(a.id || a.assetId)
              const det = (details[id] || a.details || {}) as Record<string, unknown>
              return {
                id,
                assetId: a.assetId,
                name: a.name,
                type: a.type,
                environment: a.environment,
                criticality: a.criticality,
                ipAddress: a.ipAddress,
                details: det,
                networkTarget: formatAssetTargetLabel({ ...a, details: det }),
              }
            })}
            searchQuery={searchQuery}
            filterAssetIds={filteredAssetIds}
            onEdit={(assetId) => {
              const asset = assets.find((x) => String(x.id || x.assetId) === assetId)
              if (asset) openEditModal(asset)
            }}
            onDelete={(assetId) => void handleDelete(assetId)}
            onScan={(asset) => {
              const full = assets.find((x) => String(x.id || x.assetId) === String(asset.id))
              if (!full) return
              const target = (details[full.id]?.hostname || details[full.id]?.ip || full.name)
              const payload = {
                organization_id: typeof organizationParam === 'object' ? (organizationParam as any).id : organizationParam,
                requested_by: (user as any)?.id,
                targets: [target],
              }
              fetch('/api/scans/request', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              })
                .then((res) => {
                  if (!res.ok) throw new Error('Failed to submit scan request')
                  alert('Scan request submitted')
                })
                .catch((e) => alert(e.message || 'Failed to submit scan request'))
            }}
            onOpenSystem={(systemId) => {
              setFocusSystemId(systemId)
              setPassportSection('overview')
              setActiveTab('information-systems')
            }}
          >
            <AssetsInventoryCard
              assets={assets}
              visibleAssets={visibleAssets}
              sortBy={sortBy}
              onSortChange={setSortBy}
              severityFilter={severityFilter}
              onSeverityFilterChange={(sev, checked) => {
                setSeverityFilter((prev) => {
                  const n = new Set(prev)
                  if (checked) n.add(sev)
                  else n.delete(sev)
                  return n
                })
              }}
              typeFilter={typeFilter}
              onTypeFilterChange={(type, checked) => {
                setTypeFilter((prev) => {
                  const n = new Set(prev)
                  if (checked) n.add(type)
                  else n.delete(type)
                  return n
                })
              }}
              criticalityFilter={criticalityFilter}
              onCriticalityFilterChange={(crit, checked) => {
                setCriticalityFilter((prev) => {
                  const n = new Set(prev)
                  if (checked) n.add(crit)
                  else n.delete(crit)
                  return n
                })
              }}
              ownerFilter={ownerFilter}
              onOwnerFilterChange={(ownerId, checked) => {
                setOwnerFilter((prev) => {
                  const n = new Set(prev)
                  if (checked) n.add(ownerId)
                  else n.delete(ownerId)
                  return n
                })
              }}
              environmentFilter={environmentFilter}
              onEnvironmentFilterChange={(env, checked) => {
                setEnvironmentFilter((prev) => {
                  const n = new Set(prev)
                  if (checked) n.add(env)
                  else n.delete(env)
                  return n
                })
              }}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              categoryView={categoryView}
              onCategoryViewChange={setCategoryView}
              availableTypes={getAvailableTypes()}
              availableCriticalities={getAvailableCriticalities()}
              availableOwners={getAvailableOwners()}
              availableEnvironments={getAvailableEnvironments()}
              selectedIds={selectedIds}
              onSelectAsset={(assetId, selected) => {
                setSelectedIds((prev) => {
                  const n = new Set(prev)
                  if (selected) n.add(assetId)
                  else n.delete(assetId)
                  return n
                })
              }}
              onSelectAll={(selected) => {
                setSelectedIds(selected ? new Set(visibleAssets.map((a) => a.id)) : new Set())
              }}
              onView={(assetId) => navigate(`/assets/${assetId}`)}
              onEdit={(asset) => openEditModal(asset)}
              onDelete={(assetId) => handleDelete(assetId)}
              onScan={(asset) => {
                const target = details[asset.id]?.hostname || details[asset.id]?.ip || asset.name
                const payload = {
                  organization_id: typeof organizationParam === 'object' ? (organizationParam as any).id : organizationParam,
                  requested_by: (user as any)?.id,
                  targets: [target],
                }
                fetch('/api/scans/request', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                })
                  .then((res) => {
                    if (!res.ok) throw new Error('Failed to submit scan request')
                    alert('Scan request submitted')
                  })
                  .catch((e) => alert((e as Error).message || 'Failed to submit scan request'))
              }}
              getRiskLevelColor={getRiskLevelColor}
              getRiskLevelIcon={getRiskLevelIcon}
              enterprisePosture={<AssetInventoryPostureSection />}
              enterpriseFiltersExtension={<AssetInventoryListSection />}
              intelligenceList={<AssetInventoryGraphSignalsSection />}
              showFilteredResultsTable={inventoryFilterActive}
            />
          </AssetInventoryExecutiveProvider>
          </div>

          <section className={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? 'mt-2' : undefined}>
          <div className={assetsPageStyles.insightsGrid}>
            <div
              className={
                featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
                  ? undefined
                  : assetsPageStyles.insightEnterprisePanel
              }
            >
            <SecurityControlsCoverageCard
              coverage={securityProtectionCoverage}
              organizationId={organizationParam}
              refreshKey={coverageRefreshKey}
              onApplyInventoryFilter={applySecurityInventoryFilter}
            />
            </div>

            <div
              className={
                featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
                  ? undefined
                  : assetsPageStyles.insightEnterprisePanel
              }
            >
            <VulnerabilityExposureCard
              assets={visibleAssets}
              posture={vulnerabilityPosture}
              trend={vulnerabilityTrend}
              postureLoading={postureLoading}
            />
            </div>

            <div
              className={
                featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
                  ? undefined
                  : assetsPageStyles.insightEnterprisePanel
              }
            >
            <AssetSystemAccountabilityCard
              accountability={assetSystemAccountability}
              onApplyFilter={applyAccountabilityFilter}
            />
            </div>
          </div>

          <div
            className={
              featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
                ? 'w-full'
                : `w-full ${assetsPageStyles.insightEnterprisePanel}`
            }
          >
            <section className={entStyles.layer} data-testid="assets-assessment-history">
              <div className={entStyles.posturePanel}>
                <h3 className={entStyles.postureTitle}>
                  {t('assets.assessmentHistory.title', 'Assessment history')}
                </h3>
                <p className={entStyles.postureSubtitle}>
                  {t(
                    'assets.assessmentHistory.subtitle',
                    'Review scanner assessments and parsed results in Reports.',
                  )}
                </p>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={() => navigate('/reports?view=assessments')}
                >
                  {t('reports.viewAllAssessments', 'View all assessments')}
                </Button>
              </div>
            </section>
          </div>
          </section>
        </div>
          </EnterpriseOperationalPage>
        ) : (
        <div className={assetsPageStyles.pageStack}>
          <AssetsOverview stats={assetsStats} />
          <div className={assetsPageStyles.horizontalRow} aria-label="Asset actions">
            <div className={assetsPageStyles.actionCardSlot}>
              <AddAssetCard onAssetAdded={fetchAssets} />
            </div>
            <div className={assetsPageStyles.actionCardSlot}>
              <ActiveScansCard />
            </div>
            <div className={assetsPageStyles.actionCardSlot}>
              <OpenVASScanCard
                assets={visibleAssets.map((a) => {
                  const id = String(a.id || a.assetId)
                  const det = (details[id] || a.details || {}) as Record<string, unknown>
                  const scanHost = resolveAssetScanHost(a, det)
                  return {
                    id,
                    assetId: a.assetId,
                    name: a.name,
                    ipAddress: scanHost || a.ipAddress,
                    scanHost,
                    type: a.type,
                    environment: a.environment,
                    details: det,
                  }
                })}
                onScanStarted={() => {
                  window.location.reload()
                }}
              />
            </div>
          </div>
          <InformationSystemInventoryFilter
            value={informationSystemFilter}
            onChange={setInformationSystemFilter}
            organizationId={organizationParam}
            token={token}
          />
          <div id="assets-inventory-results">
          {securityFilterChips}
          {accountabilityFilterChips}
          <AssetsInventoryCard
            assets={assets}
            visibleAssets={visibleAssets}
            sortBy={sortBy}
            onSortChange={setSortBy}
            severityFilter={severityFilter}
            onSeverityFilterChange={(sev, checked) => {
              setSeverityFilter((prev) => {
                const n = new Set(prev)
                if (checked) n.add(sev)
                else n.delete(sev)
                return n
              })
            }}
            typeFilter={typeFilter}
            onTypeFilterChange={(type, checked) => {
              setTypeFilter((prev) => {
                const n = new Set(prev)
                if (checked) n.add(type)
                else n.delete(type)
                return n
              })
            }}
            criticalityFilter={criticalityFilter}
            onCriticalityFilterChange={(crit, checked) => {
              setCriticalityFilter((prev) => {
                const n = new Set(prev)
                if (checked) n.add(crit)
                else n.delete(crit)
                return n
              })
            }}
            ownerFilter={ownerFilter}
            onOwnerFilterChange={(ownerId, checked) => {
              setOwnerFilter((prev) => {
                const n = new Set(prev)
                if (checked) n.add(ownerId)
                else n.delete(ownerId)
                return n
              })
            }}
            environmentFilter={environmentFilter}
            onEnvironmentFilterChange={(env, checked) => {
              setEnvironmentFilter((prev) => {
                const n = new Set(prev)
                if (checked) n.add(env)
                else n.delete(env)
                return n
              })
            }}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            availableTypes={getAvailableTypes()}
            availableCriticalities={getAvailableCriticalities()}
            availableOwners={getAvailableOwners()}
            availableEnvironments={getAvailableEnvironments()}
            selectedIds={selectedIds}
            onSelectAsset={(assetId, selected) => {
              setSelectedIds((prev) => {
                const n = new Set(prev)
                if (selected) n.add(assetId)
                else n.delete(assetId)
                return n
              })
            }}
            onSelectAll={(selected) => {
              setSelectedIds(selected ? new Set(visibleAssets.map((a) => a.id)) : new Set())
            }}
            onView={(assetId) => navigate(`/assets/${assetId}`)}
            onEdit={(asset) => openEditModal(asset)}
            onDelete={(assetId) => handleDelete(assetId)}
            onScan={(asset) => {
              const target = details[asset.id]?.hostname || details[asset.id]?.ip || asset.name
              const payload = {
                organization_id:
                  typeof organizationParam === 'object' ? (organizationParam as any).id : organizationParam,
                requested_by: (user as any)?.id,
                targets: [target],
              }
              fetch('/api/scans/request', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              })
                .then((res) => {
                  if (!res.ok) throw new Error('Failed to submit scan request')
                  alert('Scan request submitted')
                })
                .catch((e) => alert(e.message || 'Failed to submit scan request'))
            }}
            getRiskLevelColor={getRiskLevelColor}
            getRiskLevelIcon={getRiskLevelIcon}
            intelligenceList={
              <AssetInventoryIntelligenceWorkspace
                assets={visibleAssets.map((a) => ({
                  id: a.id || a.assetId,
                  assetId: a.assetId,
                  name: a.name,
                  type: a.type,
                  environment: a.environment,
                  criticality: a.criticality,
                  ipAddress: a.ipAddress,
                }))}
                searchQuery={searchQuery}
                categoryView={categoryView}
                onCategoryViewChange={setCategoryView}
                filterAssetIds={filteredAssetIds}
                onEdit={(assetId) => {
                  const asset = assets.find((x) => String(x.id || x.assetId) === assetId)
                  if (asset) openEditModal(asset)
                }}
                onScan={(asset) => {
                  const full = assets.find((x) => String(x.id || x.assetId) === String(asset.id))
                  if (!full) return
                  const target = details[full.id]?.hostname || details[full.id]?.ip || full.name
                  const payload = {
                    organization_id:
                      typeof organizationParam === 'object' ? (organizationParam as any).id : organizationParam,
                    requested_by: (user as any)?.id,
                    targets: [target],
                  }
                  fetch('/api/scans/request', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                  })
                    .then((res) => {
                      if (!res.ok) throw new Error('Failed to submit scan request')
                      alert('Scan request submitted')
                    })
                    .catch((e) => alert(e.message || 'Failed to submit scan request'))
                }}
                onOpenSystem={(systemId) => {
                  setFocusSystemId(systemId)
                  setPassportSection('overview')
                  setActiveTab('information-systems')
                }}
              />
            }
          />
          </div>
          <div className={assetsPageStyles.insightsGrid}>
            <div className={assetsPageStyles.insightEnterprisePanel}>
              <SecurityControlsCoverageCard
              coverage={securityProtectionCoverage}
              organizationId={organizationParam}
              refreshKey={coverageRefreshKey}
              onApplyInventoryFilter={applySecurityInventoryFilter}
            />
            </div>
            <div className={assetsPageStyles.insightEnterprisePanel}>
              <VulnerabilityExposureCard
              assets={visibleAssets}
              posture={vulnerabilityPosture}
              trend={vulnerabilityTrend}
              postureLoading={postureLoading}
            />
            </div>
            <div className={assetsPageStyles.insightEnterprisePanel}>
              <AssetSystemAccountabilityCard
                accountability={assetSystemAccountability}
                onApplyFilter={applyAccountabilityFilter}
              />
            </div>
          </div>
          <div className={`w-full ${assetsPageStyles.insightEnterprisePanel}`}>
            <section className={entStyles.layer} data-testid="assets-assessment-history-legacy">
              <div className={entStyles.posturePanel}>
                <h3 className={entStyles.postureTitle}>
                  {t('assets.assessmentHistory.title', 'Assessment history')}
                </h3>
                <p className={entStyles.postureSubtitle}>
                  {t(
                    'assets.assessmentHistory.subtitle',
                    'Review scanner assessments and parsed results in Reports.',
                  )}
                </p>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={() => navigate('/reports?view=assessments')}
                >
                  {t('reports.viewAllAssessments', 'View all assessments')}
                </Button>
              </div>
            </section>
          </div>
        </div>
        )}
          </AssetInventoryTab>
          )}
        </AssetsWorkspace>
      )}
      <div className={assetsPageStyles.scanJobsFooter}>
        <ScanJobsResultsSection defaultOrganizationId={organizationId || undefined} />
      </div>
    </PageLayout>
    
    {/* Edit Asset Modal */}
    {editOpen && editId && editAsset && (
      <EditAssetCard
        asset={{
          id: editId,
          name: editAsset.name || '',
          type: editAsset.type || '',
          environment: editAsset.environment || '',
          owner: editAsset.owner || null,
          criticality: editAsset.criticality || '',
          tags: editAsset.tags || [],
          posture: editAsset.posture || undefined,
          riskScore: editAsset.riskScore || undefined,
          complianceScore: editAsset.complianceScore || undefined,
          details: editAsset.details || {}
        }}
        onAssetUpdated={handleAssetUpdated}
        onClose={closeEditModal}
      />
    )}
    </>
  )
}

export default Assets
